/**
 * LYCHEE-SIGN-V1 canonical-string construction. Pure, framework-free, so it can be unit-tested
 * against the committed TEST_VECTORS.md byte-for-byte. MUST stay identical to the Java
 * CanonicalRequestBuilder and the other clients. See TEST_VECTORS.md at the repo root.
 */

export const VERSION_TAG = 'LYCHEE-SIGN-V1';

/** SHA-256 of the empty byte array (GET/DELETE bodies and `none` mode). */
export const EMPTY_BODY_SHA256_HEX =
  'e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855';

const HEX_UPPER = '0123456789ABCDEF';

function isHex(c: string): boolean {
  return (c >= '0' && c <= '9') || (c >= 'a' && c <= 'f') || (c >= 'A' && c <= 'F');
}

function hexVal(c: string): number {
  if (c >= '0' && c <= '9') { return c.charCodeAt(0) - 48; }
  if (c >= 'a' && c <= 'f') { return c.charCodeAt(0) - 87; }
  return c.charCodeAt(0) - 55;
}

/** Decode only %XX escapes to raw bytes; '+' stays literal (never a space). */
function decodePercent(s: string): Uint8Array {
  const out: number[] = [];
  const enc = new TextEncoder();
  for (let i = 0; i < s.length; i++) {
    const c = s.charAt(i);
    if (c === '%' && i + 2 < s.length && isHex(s.charAt(i + 1)) && isHex(s.charAt(i + 2))) {
      out.push((hexVal(s.charAt(i + 1)) << 4) | hexVal(s.charAt(i + 2)));
      i += 2;
    } else {
      enc.encode(c).forEach(b => out.push(b));
    }
  }
  return new Uint8Array(out);
}

/** Percent-encode bytes leaving only the unreserved set A-Za-z0-9-_.~ literal. */
function encodeUnreserved(bytes: Uint8Array): string {
  let sb = '';
  for (const b of bytes) {
    const c = String.fromCharCode(b);
    if ((c >= 'A' && c <= 'Z') || (c >= 'a' && c <= 'z') || (c >= '0' && c <= '9')
        || c === '-' || c === '_' || c === '.' || c === '~') {
      sb += c;
    } else {
      sb += '%' + HEX_UPPER[(b >> 4) & 0xF] + HEX_UPPER[b & 0xF];
    }
  }
  return sb;
}

/**
 * Canonicalize a raw wire query string: split on '&', split each on first '=', decode-%XX then
 * re-encode with the unreserved set, sort encoded `name=value` by byte order, join with '&'.
 * A token with no '=' becomes `name=`. Returns '' for empty input.
 */
export function canonicalizeQuery(rawQuery: string | null | undefined): string {
  if (!rawQuery) {
    return '';
  }
  const pairs: string[] = [];
  for (const token of rawQuery.split('&')) {
    if (token === '') {
      continue;
    }
    const eq = token.indexOf('=');
    const name = eq < 0 ? token : token.substring(0, eq);
    const value = eq < 0 ? '' : token.substring(eq + 1);
    pairs.push(encodeUnreserved(decodePercent(name)) + '=' + encodeUnreserved(decodePercent(value)));
  }
  pairs.sort();
  return pairs.join('&');
}

/** SHA-256 of the UTF-8 bytes of `text`, lowercase hex (Web Crypto). */
export async function sha256Hex(text: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return Array.from(new Uint8Array(digest)).map(b => b.toString(16).padStart(2, '0')).join('');
}

/** Build the 9-line canonical string (each line terminated by a single '\n', trailing newline included). */
export function buildCanonicalString(
  method: string, appRelativePath: string, rawQuery: string | null | undefined,
  bodyMode: string, bodyHashHex: string, timestampMs: string,
  nonce: string, clientId: string, keyId: string): string {
  return [
    VERSION_TAG,
    method.toUpperCase(),
    appRelativePath,
    canonicalizeQuery(rawQuery),
    bodyMode + ':' + bodyHashHex,
    timestampMs,
    nonce,
    clientId,
    keyId,
  ].map(line => line + '\n').join('');
}

/**
 * Split an absolute URL into the app-relative path (context `/rest/lychee-v2` stripped, leading
 * '/') and the raw query string (without '?'). Path is left in raw on-the-wire form.
 */
export function splitUrl(absoluteUrl: string, contextPath: string): { path: string; query: string } {
  const hashless = absoluteUrl.split('#')[0];
  const qIdx = hashless.indexOf('?');
  const beforeQuery = qIdx < 0 ? hashless : hashless.substring(0, qIdx);
  const query = qIdx < 0 ? '' : hashless.substring(qIdx + 1);

  // Strip scheme://host, then the context path.
  const schemeIdx = beforeQuery.indexOf('://');
  let pathPart = beforeQuery;
  if (schemeIdx >= 0) {
    const slash = beforeQuery.indexOf('/', schemeIdx + 3);
    pathPart = slash < 0 ? '' : beforeQuery.substring(slash);
  }
  const ctxIdx = pathPart.indexOf(contextPath);
  if (ctxIdx >= 0) {
    pathPart = pathPart.substring(ctxIdx + contextPath.length);
  }
  if (!pathPart.startsWith('/')) {
    pathPart = '/' + pathPart;
  }
  return { path: pathPart, query };
}

/** Lowercase-hex CSPRNG nonce of `bytes` bytes (>=16). */
export function generateNonceHex(bytes: number = 16): string {
  const buf = new Uint8Array(bytes);
  crypto.getRandomValues(buf);
  return Array.from(buf).map(b => b.toString(16).padStart(2, '0')).join('');
}

/** Base64 (standard, padded) of an ArrayBuffer. */
export function base64FromArrayBuffer(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (const b of bytes) {
    binary += String.fromCharCode(b);
  }
  return btoa(binary);
}
